/**
 * Engagements: the list row, the workspace's detail, and the statuses.
 */
import { z } from 'zod';
import {
	auditStatusSchema,
	exceptionStatusSchema,
	fileStatusSchema,
	reportStatusSchema,
	runStatusSchema,
	severitySchema,
} from './enums';
import { moduleOrDefaultSchema } from './modules';

/** `auditService.listAudits` — one row per audit, for the list screen. */
export const auditSummarySchema = z.object({
	pkAuditId: z.string(),
	name: z.string(),
	status: auditStatusSchema,
	periodStart: z.string(),
	periodEnd: z.string(),
	fkClientId: z.string(),
	clientName: z.string(),
	clientCode: z.string(),
	module: moduleOrDefaultSchema,
	filesTotal: z.number(),
	openExceptions: z.number(),
	blockingExceptions: z.number(),
	createdAt: z.string(),
	/* Who created it, and the last edit by a person. */
	createdByName: z.string().nullable(),
	lastEditedAt: z.string().nullable(),
	lastEditedByName: z.string().nullable(),
});

/** A file as it appears inside `AuditDetail.files` — `auditService.getAudit`. */
export const auditFileRowSchema = z.object({
	pkFileId: z.string(),
	originalName: z.string(),
	status: fileStatusSchema,
	rowCount: z.number().nullable(),
	sizeBytes: z.number().nullable(),
	createdAt: z.string(),
	/* Set when the file was deleted since; the audit keeps the rows it froze. */
	deletedAt: z.string().nullable().optional(),
});

/** An audit's (or report's) data scope, described by the server. */
export const dataScopeSchema = z.object({
	/** False when the scope was locked before filters were recorded. */
	recorded: z.boolean(),
	conditions: z.array(z.string()),
	files: z.array(z.object({ pkFileId: z.string(), name: z.string() })),
});

/** One analysis as frozen onto a report version. */
export const reportAnalysisSchema = z.object({
	slug: z.string(),
	code: z.string(),
	name: z.string(),
	kind: z.enum(['SUMMARY', 'TEST']),
	status: z.string(),
	findings: z.number().nullable(),
	resultRows: z.number().nullable(),
	lastRunAt: z.string().nullable(),
	outOfDate: z.boolean().catch(false),
});

/**
 * `auditService.getAudit` — everything the workspace's context bar and tabs
 * need on arrival, in one round trip.
 *
 * `files_total` is dropped from the summary and replaced by `files` itself:
 * the list screen only needs a count, the workspace needs the rows.
 */
export const auditDetailSchema = auditSummarySchema
	/* The list's own columns; one audit's response does not carry them. */
	.omit({
		filesTotal: true,
		createdByName: true,
		lastEditedAt: true,
		lastEditedByName: true,
	})
	.extend({
		files: z.array(auditFileRowSchema),
		latestReportVersion: z.number().nullable(),
		latestReportStatus: reportStatusSchema.nullable(),
		readiness: z.object({
			ready: z.boolean(),
			blockers: z.array(z.string()),
		}),
		/* `null` means NOT RECORDED — frozen before it was stored — which is a
     different claim from an empty filter, and must be rendered as such. */
		dataFilter: z.unknown(),
		/** The same filter described server-side, chosen files named. */
		dataScope: dataScopeSchema,
		/** When the data scope was last locked; null for older audits. */
		scopeLockedAt: z.string().nullable().catch(null),
		/** Rows actually frozen into the snapshot. */
		rowCount: z.number(),
	});

export type AuditSummary = z.infer<typeof auditSummarySchema>;
export type AuditFileRow = z.infer<typeof auditFileRowSchema>;
export type AuditDetail = z.infer<typeof auditDetailSchema>;

/** One catalogue entry as `analysisService.listAuditRuns` returns it — the
 *  static definition spread with this audit's most recent run against it, or
 *  `null` if it has never been run here. */
export const analysisCatalogueEntrySchema = z.object({
	slug: z.string(),
	code: z.string(),
	name: z.string(),
	primitive: z.string(),
	blurb: z.string(),
	requires: z.array(z.string()),
	lastRun: z
		.object({
			pkAnalysisRunId: z.string(),
			status: runStatusSchema,
			rowsRead: z.number().nullable(),
			errorMessage: z.string().nullable(),
			finishedAt: z.string().nullable(),
		})
		.nullable(),
});

/** `exceptionService.present()`. Outlives the run that raised it. */
export const auditExceptionSchema = z.object({
	pkAuditExceptionId: z.string(),
	fkAuditId: z.string(),
	analysisSlug: z.string(),
	groupKey: z.string(),
	title: z.string(),
	detail: z.string(),
	severity: severitySchema,
	status: exceptionStatusSchema,
	rowsAffected: z.number(),
	valueAffected: z.number().nullable(),
	isBlocking: z.boolean(),
	resolutionNote: z.string(),
	resolvedAt: z.string().nullable(),
	createdAt: z.string(),
});

/** One row of a report's checklist, frozen into the report at generation. */
export const checklistItemSchema = z.object({
	key: z.string(),
	label: z.string(),
	passed: z.boolean(),
	detail: z.string(),
	blocking: z.boolean(),
});

/** The headline figures off the reconciliation run. Null, not zero, when the
 *  run has not happened. */
export const reportPositionSchema = z.object({
	matched_returns: z.number().nullable(),
	flagged_returns: z.number().nullable(),
	flagged_value: z.number().nullable(),
	match_rate_pct: z.number().nullable(),
});

/** `exceptionService.summarise()`, frozen onto the report at generation. */
export const exceptionSummarySchema = z.object({
	total: z.number(),
	open: z.number(),
	accepted: z.number(),
	corrected: z.number(),
	closed: z.number(),
	blocking: z.number(),
	openValue: z.number(),
});

/**
 * `reportService.present()`.
 *
 * `approved_by_id` and `issued_by_id` are both real fields: approving and
 * issuing are different acts, possibly by different people, and each is
 * attributed separately. Neither is optional — both are always present, `null`
 * until that transition happens.
 */
export const reportSchema = z.object({
	pkReportId: z.string(),
	fkAuditId: z.string(),
	version: z.number(),
	status: reportStatusSchema,
	checklist: z.array(checklistItemSchema),
	position: reportPositionSchema,
	exceptionSummary: exceptionSummarySchema,
	/* The filter that produced THIS report's figures, copied at generation.
     `null` means not recorded — an older report, or an audit frozen before the
     filter was stored. Never render null as "no filter". */
	dataFilter: z.unknown(),
	dataScope: dataScopeSchema,
	/** The analyses this version was generated over; null for older versions. */
	analyses: z.array(reportAnalysisSchema).nullable().catch(null),
	overrideReason: z.string(),
	fkApprovedById: z.string().nullable(),
	fkIssuedById: z.string().nullable(),
	generatedByName: z.string().nullable().catch(null),
	approvedByName: z.string().nullable().catch(null),
	issuedByName: z.string().nullable().catch(null),
	approvedAt: z.string().nullable(),
	issuedAt: z.string().nullable(),
	createdAt: z.string(),
});

export type AnalysisCatalogueEntry = z.infer<
	typeof analysisCatalogueEntrySchema
>;
export type AuditException = z.infer<typeof auditExceptionSchema>;
export type ChecklistItem = z.infer<typeof checklistItemSchema>;
export type ReportPosition = z.infer<typeof reportPositionSchema>;
export type ExceptionSummary = z.infer<typeof exceptionSummarySchema>;
export type Report = z.infer<typeof reportSchema>;

/**
 * A file as the audit's data tab lists it — `GET /audits/:id/files`.
 *
 * Richer than `AuditFileRow`, which is what `getAudit` embeds: this carries
 * the per-file classification coverage the tab draws a bar from.
 */
export const selectedFileSchema = z.object({
	pkFileId: z.string(),
	originalName: z.string(),
	status: fileStatusSchema,
	rowCount: z.number().nullable(),
	sizeBytes: z.number().nullable(),
	periodLabel: z.string().nullable(),
	createdAt: z.string(),
	/* Set when the file was deleted since; the audit keeps the rows it froze. */
	deletedAt: z.string().nullable().optional(),
	coverage: z.object({
		rows: z.number(),
		classified: z.number(),
		unclassified: z.number(),
	}),
});

export type SelectedFile = z.infer<typeof selectedFileSchema>;

export type DataScope = z.infer<typeof dataScopeSchema>;
export type ReportAnalysis = z.infer<typeof reportAnalysisSchema>;
